import { useEffect, useState, type FormEvent } from 'react';
import { signIn, signUp } from '../lib/auth';
import { fetchPublicConfig } from '../lib/config';
import { Icon } from './Icon';

// Auth gate.
export function LoginView() {
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  // Hide the register UI when the API has registration disabled. The API still
  // enforces the block server-side; this just avoids showing a dead-end form.
  useEffect(() => {
    let cancelled = false;
    void fetchPublicConfig().then(({ registrationEnabled }) => {
      if (cancelled) return;
      setRegistrationEnabled(registrationEnabled);
      if (!registrationEnabled) setMode('in');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'in') {
        const res = await signIn.email({ email, password });
        if (res.error) throw new Error(res.error.message ?? 'Nie udało się zalogować');
      } else {
        const res = await signUp.email({ email, password, name: name.trim() || email.split('@')[0]! });
        if (res.error) throw new Error(res.error.message ?? 'Nie udało się utworzyć konta');
      }
      // On success better-auth refreshes the session and the gate swaps to the app.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił błąd');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          <div className="logo-mark">»</div>
          <div>
            <div className="auth-title">Delvero Sales OS</div>
            <div className="auth-sub">{mode === 'in' ? 'Zaloguj się do panelu' : 'Utwórz nowe konto'}</div>
          </div>
        </div>

        {error ? (
          <div className="auth-error">
            <Icon name="flag" size={14} />
            <span>{error}</span>
          </div>
        ) : null}

        <form onSubmit={submit}>
          {mode === 'up' ? (
            <label className="fld">
              Imię i nazwisko
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="np. Jan Kowalski" />
            </label>
          ) : null}
          <label className="fld">
            E-mail
            <input
              className="input"
              type="email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="fld">
            Hasło
            <input
              className="input"
              type="password"
              value={password}
              autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
            />
          </label>
          <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', height: 38 }}>
            {busy ? 'Proszę czekać…' : mode === 'in' ? 'Zaloguj się' : 'Utwórz konto'}
          </button>
        </form>

        {registrationEnabled ? (
          <div className="auth-foot">
            {mode === 'in' ? (
              <>
                Nie masz konta?{' '}
                <button onClick={() => { setMode('up'); setError(null); }}>Zarejestruj się</button>
              </>
            ) : (
              <>
                Masz już konto?{' '}
                <button onClick={() => { setMode('in'); setError(null); }}>Zaloguj się</button>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
